/**
 * Player-board ordering for the NFL draft room.
 *
 * Lives in lib/ rather than beside the table component because a non-component export from
 * a component file breaks react-refresh (the same reason nflDraftBoardColors was extracted).
 *
 * ── HIGHER IS BETTER ────────────────────────────────────────────────────────
 *
 * `proj_sab_pts` is an INVERTED-scoring projection, so the best available player has the
 * LARGEST value and `trash` sorts descending. Sorting this like a conventional fantasy rank
 * would make the board recommend the strongest real players in a league that punishes
 * exactly that. SAB-458 fixed this same inversion on the server (the auto-pick waterfall
 * used to order by `adp ASC`); the identical trap exists on the client.
 */

export interface SortablePlayer {
  proj_sab_pts?: number | null;
  adp?: number | null;
  bye_week?: number | null;
}

export type PlayerSortKey = 'trash' | 'adp' | 'bye';

/**
 * Returns a NEW array; the input is not mutated.
 *
 * Nulls always sort LAST, in every mode and regardless of their input position. A player
 * with no projection is unmeasured, not worst-available — floating them to either end
 * misrepresents them. The comparator returns a consistent sign for null pairs so the result
 * does not depend on the engine's sort stability.
 */
export function sortPlayers<T extends SortablePlayer>(rows: T[], key: PlayerSortKey): T[] {
  const val = (p: T) => (key === 'trash' ? p.proj_sab_pts : key === 'adp' ? p.adp : p.bye_week);
  // trash: higher first. adp/bye: lower first.
  const dir = key === 'trash' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = val(a), bv = val(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return (av - bv) * dir;
  });
}
